"use client";

import { motion } from "framer-motion";
import { Mic } from "lucide-react";

type StartOnlyPracticeEntryProps = {
  pending: boolean;
  onStart: () => void;
  primaryButtonClassName: string;
  primaryButtonHoverClassName: string;
  targetLanguageName: string;
  instructionLanguageName: string;
};

export function StartOnlyPracticeEntry({
  pending,
  onStart,
  primaryButtonClassName,
  primaryButtonHoverClassName,
  targetLanguageName,
  instructionLanguageName,
}: StartOnlyPracticeEntryProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex w-full max-w-2xl flex-col items-center rounded-2xl border border-white/10 bg-black/35 px-6 py-10 shadow-2xl shadow-black/30 backdrop-blur-sm sm:px-10 sm:py-14"
      data-pre-session-variant="start-only"
    >
      <div className="mb-6 flex flex-wrap items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-orange-100/80">
        <span className="rounded-full border border-orange-300/25 bg-orange-400/10 px-3 py-1.5">
          Beginner {targetLanguageName}
        </span>
        <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-white/65">
          Guided from {instructionLanguageName}
        </span>
      </div>
      <h1 className="max-w-xl text-4xl font-black leading-tight tracking-normal text-white sm:text-5xl">
        Ready to speak {targetLanguageName}?
      </h1>
      <p className="mt-4 max-w-lg text-base font-medium leading-7 text-zinc-300 sm:text-lg">
        Your tutor will guide you in {instructionLanguageName} and help you answer in beginner{" "}
        {targetLanguageName}.
      </p>
      <motion.button
        type="button"
        onClick={onStart}
        disabled={pending}
        className={`group relative mt-8 min-h-20 w-full max-w-md overflow-hidden rounded-lg px-8 py-5 transition-colors disabled:cursor-wait disabled:opacity-80 ${primaryButtonClassName} ${primaryButtonHoverClassName}`}
        whileHover={pending ? undefined : { y: -2 }}
        whileTap={pending ? undefined : { scale: 0.98 }}
      >
        <div className="absolute inset-0 translate-y-full bg-white/20 transition-transform duration-300 group-hover:translate-y-0" />
        <div className="relative flex items-center justify-center gap-3 text-2xl font-black text-inherit">
          {pending ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
            >
              <Mic size={25} />
            </motion.div>
          ) : (
            <Mic size={25} />
          )}
          {pending ? `Preparing ${targetLanguageName} practice...` : "Start"}
        </div>
      </motion.button>
      <p className="mt-4 text-xs font-medium text-white/40">
        Your browser may ask for microphone access.
      </p>
    </motion.div>
  );
}
